import React, { useState } from 'react';
import { useMutation } from 'react-apollo';
import { useToasts } from 'react-toast-notifications';
import PropTypes from 'prop-types';
import { map } from 'lodash';
import {
  Modal, ModalBody, Button, Form, FormGroup, Label, Input,
} from 'reactstrap';
import ReactImageFallback from 'react-image-fallback';
import {
  staticConstants, FALLBACK_ICON, handleGraphqlError, capitalizeEveryWord,
} from '../../utils';
import { UPDATE_STATUS, LOADER_MUTATION } from '../../graphql-client/mutations';
import { GET_ADMIN_NOTIFICATION } from '../../graphql-client/queries';

function Request(props) {
  const { addToast } = useToasts();
  const {
    isOpen, onClose, userDetail, userRole, updateUserList,
  } = props;
  const [modal, setModal] = useState(isOpen);
  const [rejectReasonIdx, setRejectReasonIdx] = useState(-1);
  const [rejectReason, setRejectReason] = useState('');
  const [otherReasonValue, setOtherReasonValue] = useState('');
  const isArtist = userRole === staticConstants.ROLE.ARTIST;
  const managerArtistData = !isArtist && userDetail.artistData[0];
  const cityLabel = isArtist ? userDetail.city.label : managerArtistData.city.label;
  const stateLabel = isArtist ? userDetail.state.label : managerArtistData.state.label;
  const countryLabel = isArtist ? userDetail.country.label : managerArtistData.country.label;
  const spotifyUrl = isArtist ? userDetail.socialMedia.spotify : managerArtistData.socialMedia.spotify;
  const twitterUrl = isArtist ? userDetail.socialMedia.twitter : managerArtistData.socialMedia.twitter;
  const weblinkUrl = isArtist ? userDetail.socialMedia.weblink : managerArtistData.socialMedia.weblink;
  const youtubeUrl = isArtist ? userDetail.socialMedia.youtube : managerArtistData.socialMedia.youtube;
  const facebookUrl = isArtist ? userDetail.socialMedia.facebook : managerArtistData.socialMedia.facebook;
  const instagramUrl = isArtist ? userDetail.socialMedia.instagram : managerArtistData.socialMedia.instagram;

  const toggle = () => {
    setModal(!modal);
    onClose();
  };

  const fetchedProfileImage = isArtist ? userDetail.artistImage : userDetail.managerImage;

  const hideModal = () => {
    toggle();
    onClose();
  };

  const [loaderMutate] = useMutation(LOADER_MUTATION, {
    variables: { show: false },
  });

  const [updateArtistStatus] = useMutation(UPDATE_STATUS, {
    variables: {},
    refetchQueries: [{ query: GET_ADMIN_NOTIFICATION }],
    onError(err) {
      loaderMutate({ variables: { show: false } });
      addToast(handleGraphqlError(err), { appearance: 'error' });
    },

    onCompleted(profileData) {
      loaderMutate({ variables: { show: false } });
      updateUserList();
      onClose();
      addToast(profileData.updateStatus.message, { appearance: 'success' });
    },
  });

  const actionOnArtistRequest = (reqObj) => {
    if (reqObj.status === staticConstants.STATUS_TYPE.REJECTED) {
      reqObj.rejectReason = {
        preDefined: rejectReason,
        custom: otherReasonValue,
      };
    }
    loaderMutate({ variables: { show: true } });
    updateArtistStatus({ variables: reqObj });
  };

  const toggleRejectReason = (defaultReason, selectedIndex) => {
    setRejectReason(rejectReasonIdx === selectedIndex ? '' : defaultReason);
    setRejectReasonIdx(rejectReasonIdx === selectedIndex ? -1 : selectedIndex);
  };

  const renderRejectReason = () => {
    const reasonList = staticConstants.REQUEST_REJECT_REASON;
    return map(reasonList, (item, index) => item && (
      <div key={`reason-${index}`} onClick={() => { toggleRejectReason(item.label, index); }}>
        <label className="reason-col" htmlFor={`reason${index + 1}`}>
          <input
            key={index}
            type="checkbox"
            checked={item.value === (rejectReasonIdx + 1)}
          />
          <span>{item.label}</span>
        </label>
      </div>
    ));
  };

  return (
    <Modal isOpen={modal} className="modal-request modal-dialog-centered">
      <ModalBody>
        <div className="modal-request-account">
          <h2>New account request</h2>
          <div className="modal-request-account-image-full">
            <ReactImageFallback
              src={fetchedProfileImage}
              fallbackImage={FALLBACK_ICON}
              className="image-cover"
              alt="User"
            />
          </div>
          <h4>
            <span className="icon-icon-location" />
            {cityLabel
              ? ` ${`${capitalizeEveryWord(`${cityLabel}`)}, `}`
              : ''}
            {stateLabel
              ? `${`${capitalizeEveryWord(`${stateLabel}`)}, `}`
              : ''}
            {countryLabel
              ? `${`${capitalizeEveryWord(`${countryLabel}`)}, `}`
              : ''}
          </h4>
          {isArtist
            ? (<h3>{capitalizeEveryWord(userDetail.name)}</h3>)
            : (
              <h3>
                {' '}
                {capitalizeEveryWord(userDetail.managerFirstName)}
                {' '}
                {capitalizeEveryWord(userDetail.managerLastName)}
              </h3>
            )}

          {isArtist
            ? userDetail.managerData && userDetail.managerData.managerFirstName
              && (
              <p>
                <em>Label:</em>
                {' '}
                {capitalizeEveryWord(userDetail.managerData.managerFirstName)}
                {' '}
                {capitalizeEveryWord(userDetail.managerData.managerLastName)}
              </p>
              )
            : userDetail.artistData && managerArtistData
            && (
            <p>
              <em>Artist:</em>
              {' '}
              {capitalizeEveryWord(managerArtistData.name)}
            </p>
            )}
          <div className="modal-request-account-info">
            <p>
              <span className="ico icon-phone" />
              {' '}
              {userDetail.contactNumber}
            </p>
            <p>
              <span className="ico icon-email" />
              {' '}
              {userDetail.email}
            </p>
          </div>
          <div className="modal-request-account-social-icons">
            {facebookUrl
            && <a className="social-icon request-icons facebook" href={facebookUrl} target="_blank" rel="noopener noreferrer" />}
            {twitterUrl
            && <a className="social-icon request-icons twitter" href={twitterUrl} target="_blank" rel="noopener noreferrer" />}
            {youtubeUrl
            && <a className="social-icon request-icons youtube" href={youtubeUrl} target="_blank" rel="noopener noreferrer" />}
            {instagramUrl
            && <a className="social-icon request-icons instagram" href={instagramUrl} target="_blank" rel="noopener noreferrer" />}
            {spotifyUrl
            && <a className="social-icon request-icons spotify" href={spotifyUrl} target="_blank" rel="noopener noreferrer" />}
            {weblinkUrl
            && <a className="social-icon request-icons weblink" href={weblinkUrl} target="_blank" rel="noopener noreferrer" />}
          </div>
        </div>
        <Form className="modal-request-content">
          <span
            className="icon icon-cross"
            onClick={hideModal}
          />
          <h4>Share your feedback</h4>
          <div className="reason-col-wrap">
            <p className="reason-col head">
              <span>{staticConstants.REQUEST_REJECT_REASON_LABEL}</span>
            </p>
          </div>
          {renderRejectReason()}
          <FormGroup>
            <Label
              for="otherReason"
            >
              Other reason for rejection
            </Label>
            <Input
              type="textarea"
              className="medium"
              name="otherReason"
              value={otherReasonValue}
              onChange={(e) => setOtherReasonValue(e.target.value)}
            />
          </FormGroup>
          <div className="modal-request-btn-group">
            <Button
              color="reject"
              disabled={!otherReasonValue && !rejectReason}
              onClick={() => {
                actionOnArtistRequest({
                  userId: userDetail._id,
                  status: staticConstants.STATUS_TYPE.REJECTED,
                });
              }}
            >
              Reject & notify
            </Button>
            <Button
              color="accept"
              onClick={() => {
                actionOnArtistRequest({
                  userId: userDetail._id,
                  status: staticConstants.STATUS_TYPE.ACTIVE,
                });
              }}
              disabled={otherReasonValue || rejectReason}
            >
              Approve account
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
}

Request.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userDetail: PropTypes.object.isRequired,
  updateUserList: PropTypes.func.isRequired,
  userRole: PropTypes.string.isRequired,
};

export default Request;
